/**
 * De context die de feedbackknop meestuurt.
 *
 * Elk scherm weet iets anders: het objectscherm kent het object en de run
 * waaruit de beleidswaarde komt, de impact-waterfall kent de twee
 * verslagjaren. Een scherm zet die context bij het laden; de knop in de schil
 * leest hem uit. Zo hoeft de schil niets van de schermen te weten en het scherm
 * niets van de knop.
 */
import { useEffect } from "react";
import { create } from "zustand";

import type { FeedbackContext } from "@/api/feedback";

type FeedbackContextStore = {
  context: FeedbackContext;
  setContext: (context: FeedbackContext) => void;
  clear: () => void;
};

export const useFeedbackContextStore = create<FeedbackContextStore>((set) => ({
  context: {},
  setContext: (context) => set({ context }),
  clear: () => set({ context: {} }),
}));

/**
 * Meldt de context van dít scherm aan de feedbackknop, en ruimt hem op bij het
 * verlaten van het scherm — anders reist de run-id van het objectscherm mee
 * naar het volgende scherm en staat er straks een verkeerde herleiding bij een
 * opmerking.
 */
export function useFeedbackContext(context: FeedbackContext): void {
  const setContext = useFeedbackContextStore((state) => state.setContext);
  const clear = useFeedbackContextStore((state) => state.clear);
  // De sleutel is de serialisatie van de context zelf: zo loopt het effect
  // alleen opnieuw als er werkelijk iets verandert, ook wanneer het scherm bij
  // elke render een nieuw object doorgeeft.
  const key = JSON.stringify(context);

  useEffect(() => {
    setContext(JSON.parse(key) as FeedbackContext);
    return clear;
  }, [key, setContext, clear]);
}
